import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router'
import { api, type NewsResponse } from '../../lib/api'
import { CategoryMark } from '../spaces/CategoryMark'
import { layout } from '../../design/tokens'

/**
 * Ce que les autres ont ajouté depuis votre dernière visite — décision L33.
 *
 * Vos propres ajouts sont exclus : on n'a pas besoin qu'on nous signale ce qu'on vient d'écrire,
 * et les compter ferait sonner la cloche à chaque fiche qu'on ajoute soi-même.
 *
 * L'ouverture de l'écran marque tout comme vu : c'est le geste attendu, et demander en plus de
 * cliquer « j'ai lu » serait une formalité de plus pour rien.
 */
export function NewsScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isPending } = useQuery<NewsResponse>({
    queryKey: ['nouveautes'],
    queryFn: api.news,
  })

  const marquerLu = useMutation({
    mutationFn: api.markNewsSeen,
    onSuccess: async () => {
      // Les espaces portent le même compteur : il doit retomber en même temps.
      await queryClient.invalidateQueries({ queryKey: ['espaces'] })
    },
  })

  const marquer = marquerLu.mutate

  useEffect(() => {
    // La liste affichée n'est pas vidée pour autant : on doit pouvoir lire ce qu'on vient de
    // découvrir. Seul le compteur retombe.
    if (!isPending) {
      marquer()
    }
  }, [isPending, marquer])

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter, paddingBottom: 32 }}
    >
      <header className="flex items-center gap-3" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="flex shrink-0 items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ←
        </button>
        <h1 className="font-serif text-screen">Nouveautés</h1>
      </header>

      {isPending ? (
        <p className="text-body text-ink-muted">Chargement…</p>
      ) : data && data.items.length > 0 ? (
        <>
          <p className="text-meta text-ink-muted" style={{ paddingBottom: 12 }}>
            Depuis le {new Date(data.since).toLocaleString('fr-FR')}.
          </p>

          <ul className="flex flex-col gap-2">
            {data.items.map((item) => (
              <li key={item.id}>
                <Link
                  to={
                    item.kind === 'event'
                      ? `/espace/${item.spaceId}/agenda`
                      : `/espace/${item.spaceId}/fiche/${item.id}`
                  }
                  className="flex items-center gap-3 rounded-card border border-border bg-card p-3 text-ink"
                >
                  {item.categoryInitials && item.categoryHue !== null ? (
                    <CategoryMark
                      initials={item.categoryInitials}
                      hue={item.categoryHue}
                      size="small"
                    />
                  ) : null}
                  <span className="grow truncate">
                    <span className="block truncate text-card-title font-semibold">
                      {item.title}
                    </span>
                    <span className="block truncate text-meta text-ink-muted">
                      {item.kind === 'event' ? 'Agenda · ' : ''}
                      {item.createdByName} · {item.spaceName}
                      {item.categoryName ? ` · ${item.categoryName}` : ''}
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          {data.count > data.items.length ? (
            <p className="mt-4 text-meta text-ink-muted">
              {data.count - data.items.length} autres, non listées ici.
            </p>
          ) : null}
        </>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">Rien de neuf.</p>
          <p className="mt-2 text-body text-ink-muted">
            Vous verrez ici ce que les autres ajoutent — fiches et rendez-vous — dans tous vos
            espaces, sans vos propres ajouts.
          </p>
        </div>
      )}
    </main>
  )
}
